import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { theme, card, btnPrimary, inputStyle } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { DimensionHeading, RulerProgress, StatusTag, type StatusTone } from "./Drawing";
import { PortalLink } from "./PortalLink";
import { FRAMEWORK_LABELS, useFrameworks, type FrameworkId } from "../context/FrameworksContext";

// The standards that apply at one site, the level it aims for in each, and how it stands against
// that target (backend lib/siteStandards.ts, routes/siteCompliance.routes.ts). The organisation's
// Statement of Applicability is untouched by anything here: a site's own standing on a control
// comes from a site-scoped compliance check or a member, and a control without one stands at the
// site as it stands for the organisation.

type TargetLevel = "certified" | "aligned" | "baseline" | "not_applicable";

const LEVEL_LABELS: Record<TargetLevel, string> = {
  certified: "In certification scope",
  aligned: "Aligned, not certified",
  baseline: "Baseline",
  not_applicable: "Does not apply here",
};
const DEFAULT_TARGET: Record<"aligned" | "baseline", number> = { aligned: 80, baseline: 50 };

interface SiteStandard {
  frameworkId: string;
  label: string;
  targetLevel: TargetLevel;
  targetScore: number | null;
  inherited: boolean;
  score: number | null;
  implemented: number;
  inProgress: number;
  total: number;
  assessedAtSite: number;
  standing: "met" | "below" | "not_scored" | "not_applicable";
}

interface SiteControlRow {
  controlId: string;
  name: string;
  frameworkId: string;
  status: string;
  note: string | null;
  aiNote: string | null;
  assessedAt: string | null;
  confirmed: boolean;
}

interface SiteComplianceResponse {
  site: { id: string; name: string; inScope: boolean };
  standards: SiteStandard[];
  met: number;
  below: number;
  controls: SiteControlRow[];
  /** The enrolled standards a site can be given a level in (not data protection laws or maturity guides). */
  available: string[];
}

const CONTROL_STATUS: Record<string, { label: string; tone: StatusTone }> = {
  COMPLIANT: { label: "Met at this site", tone: "met" },
  IN_PROGRESS: { label: "In progress", tone: "partial" },
  GAP: { label: "Gap", tone: "gap" },
  NOT_APPLICABLE: { label: "Not applicable here", tone: "none" },
};

const STANDING: Record<SiteStandard["standing"], { label: string; tone: StatusTone }> = {
  met: { label: "Meets its target", tone: "met" },
  below: { label: "Below its target", tone: "gap" },
  not_scored: { label: "Not scored", tone: "none" },
  not_applicable: { label: "Does not apply", tone: "none" },
};

const labelOf = (id: string) => FRAMEWORK_LABELS[id as FrameworkId] ?? id;

export function SiteStandardsPanel({ siteId, canSetStandards, canSetControls }: {
  siteId: string;
  canSetStandards: boolean;
  canSetControls: boolean;
}) {
  const [data, setData] = useState<SiteComplianceResponse | null>(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, { level: TargetLevel; score: number | null }>>({});
  const [saving, setSaving] = useState(false);
  const [busyControl, setBusyControl] = useState<string | null>(null);

  function load() {
    api.get<SiteComplianceResponse>(`/site-compliance/sites/${siteId}`)
      .then((d) => { setData(d); setError(""); })
      .catch((e) => setError(apiErrorMessage(e, "Could not load this site's standards.")));
  }
  useEffect(load, [siteId]); // eslint-disable-line react-hooks/exhaustive-deps

  function startEdit() {
    const next: typeof draft = {};
    // A standard the site has no level for yet starts as "does not apply here". Saving always sends
    // every standard, so "nothing applies" is never mistaken for "follow the organisation".
    for (const fw of data?.available ?? []) next[fw] = { level: "not_applicable", score: null };
    for (const s of data?.standards ?? []) next[s.frameworkId] = { level: s.targetLevel, score: s.targetScore };
    setDraft(next);
    setEditing(true);
  }

  async function save(followOrganisation = false) {
    setSaving(true);
    try {
      const available = new Set(data?.available ?? []);
      const standards = followOrganisation ? null : Object.entries(draft)
        .filter(([frameworkId]) => available.has(frameworkId))
        .map(([frameworkId, v]) => ({
          frameworkId,
          targetLevel: v.level,
          targetScore: v.level === "aligned" || v.level === "baseline" ? (v.score ?? DEFAULT_TARGET[v.level]) : null,
        }));
      await api.put(`/site-compliance/sites/${siteId}/standards`, { standards });
      setEditing(false);
      load();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not save this site's standards."));
    } finally {
      setSaving(false);
    }
  }

  async function setControl(controlId: string, status: string | null) {
    setBusyControl(controlId);
    try {
      if (status === null) await api.del(`/site-compliance/sites/${siteId}/controls/${encodeURIComponent(controlId)}`);
      else await api.put(`/site-compliance/sites/${siteId}/controls/${encodeURIComponent(controlId)}`, { status });
      load();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not update the control."));
    } finally {
      setBusyControl(null);
    }
  }

  const standards = data?.standards ?? [];
  const inherited = standards.length > 0 && standards.every((s) => s.inherited);

  return (
    <div style={{ ...card, padding: "14px 18px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
        <DimensionHeading as="h3" count={standards.filter((s) => s.targetLevel !== "not_applicable").length} style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, flex: 1, minWidth: 0, margin: 0 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><ShieldCheck size={14} />Standards at this site</span>
        </DimensionHeading>
        {standards.some((s) => s.targetLevel !== "not_applicable") && (
          <PortalLink to={`/compliance?site=${encodeURIComponent(siteId)}`} style={{ fontSize: 12, color: theme.color.accentText, textDecoration: "none", whiteSpace: "nowrap" }}>
            Check this site →
          </PortalLink>
        )}
        {canSetStandards && !editing && (
          <button type="button" onClick={startEdit} style={{ ...btnPrimary, padding: "4px 10px", fontSize: 12 }}>Set standards</button>
        )}
      </div>

      {error && <div style={{ fontSize: 12, color: theme.color.dangerText, marginBottom: 8 }}>{error}</div>}

      {!editing && data && (
        <>
          <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 8, lineHeight: 1.5 }}>
            {standards.length === 0
              ? (data.site.inScope
                ? "No standard applies here yet. Set the standards this site works to and how far it means to meet each."
                : "This site is outside the certification scope, so no standard applies here unless you set one.")
              : inherited
                ? "This site follows the organisation: every enrolled standard, at the certified level where the standard is certifiable. Set its own standards to give it a different target."
                : "Each standard's target is this site's own. A control without a site result stands here as it stands for the organisation."}
          </div>
          {standards.map((s) => {
            const standing = STANDING[s.standing];
            return (
              <div key={s.frameworkId} style={{ padding: "8px 0", borderBottom: `1px solid ${theme.color.border}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 13 }}>
                  <span style={{ fontWeight: 600, color: theme.color.heading, flex: 1, minWidth: 160 }}>{s.label}</span>
                  <span style={{ fontSize: 12, color: theme.color.muted }}>
                    {LEVEL_LABELS[s.targetLevel]}
                    {s.targetLevel !== "not_applicable" && s.targetScore != null && ` · target ${s.targetScore}%`}
                  </span>
                  <StatusTag tone={standing.tone}>{standing.label}</StatusTag>
                </div>
                {s.score != null && (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6 }}>
                    <RulerProgress value={s.score} label={`${s.label} at this site: ${s.score}%`} style={{ flex: 1 }}
                      fill={s.standing === "met" ? "var(--c-successText)" : "var(--c-warningText)"} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: theme.color.heading, minWidth: 40, textAlign: "right" }}>{s.score}%</span>
                  </div>
                )}
                {s.score != null && (
                  <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 4 }}>
                    {s.implemented} of {s.total} applicable controls met here{s.inProgress ? `, ${s.inProgress} in progress` : ""} · {s.assessedAtSite} with a result of this site's own
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {editing && (
        <div>
          <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 8 }}>
            Choose how far this site means to meet each of the organisation's standards. Data protection laws and maturity guides belong to the organisation and are not set per site.
          </div>
          {(data?.available ?? []).length === 0 && (
            <div style={{ fontSize: 12, color: theme.color.faint, padding: "6px 0" }}>The organisation is not enrolled in a standard that can be set per site.</div>
          )}
          {(data?.available ?? []).map((fw) => {
            const v = draft[fw] ?? { level: "not_applicable" as TargetLevel, score: null };
            return (
              <div key={fw} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: `1px solid ${theme.color.border}`, flexWrap: "wrap" }}>
                <span style={{ flex: 1, minWidth: 160, fontSize: 13, color: theme.color.text }}>{labelOf(fw)}</span>
                <select
                  aria-label={`Level for ${labelOf(fw)}`}
                  value={v.level}
                  onChange={(e) => setDraft((d) => ({ ...d, [fw]: { level: e.target.value as TargetLevel, score: d[fw]?.score ?? null } }))}
                  style={{ ...inputStyle, width: "auto", minWidth: 200 }}
                >
                  {(Object.keys(LEVEL_LABELS) as TargetLevel[]).map((l) => <option key={l} value={l}>{LEVEL_LABELS[l]}</option>)}
                </select>
                {(v.level === "aligned" || v.level === "baseline") && (
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: theme.color.muted }}>
                    Target
                    <input
                      type="number" min={1} max={100}
                      value={v.score ?? DEFAULT_TARGET[v.level]}
                      onChange={(e) => setDraft((d) => ({ ...d, [fw]: { ...v, score: Math.max(1, Math.min(100, Number(e.target.value) || 1)) } }))}
                      style={{ ...inputStyle, width: 70 }}
                    />%
                  </label>
                )}
              </div>
            );
          })}
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <button type="button" disabled={saving || (data?.available ?? []).length === 0} onClick={() => save(false)} style={{ ...btnPrimary, opacity: saving || (data?.available ?? []).length === 0 ? 0.6 : 1 }}>Save</button>
            <button type="button" disabled={saving} onClick={() => save(true)}
              style={{ padding: "6px 12px", borderRadius: 3, border: `1px solid ${theme.color.borderStrong}`, background: "transparent", color: theme.color.text, fontSize: 12, cursor: "pointer" }}>
              Follow the organisation
            </button>
            <button type="button" disabled={saving} onClick={() => setEditing(false)}
              style={{ padding: "6px 12px", borderRadius: 3, border: "none", background: "transparent", color: theme.color.muted, fontSize: 12, cursor: "pointer" }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {!editing && data && data.controls.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.muted, textTransform: "uppercase", letterSpacing: "0.05em", padding: "6px 0 2px" }}>
            Results of this site's own
          </div>
          {data.controls.map((c) => {
            const st = CONTROL_STATUS[c.status] ?? CONTROL_STATUS.GAP;
            return (
              <div key={c.controlId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: `1px solid ${theme.color.border}`, fontSize: 13, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, fontVariantNumeric: "tabular-nums", color: theme.color.accentText, flexShrink: 0 }}>{c.controlId}</span>
                <span style={{ flex: 1, minWidth: 140 }} title={c.aiNote ?? undefined}>
                  {c.name}
                  <span style={{ display: "block", fontSize: 11, color: theme.color.faint }}>
                    {labelOf(c.frameworkId)} · {c.confirmed ? "set by a member" : "from a site check, awaiting confirmation"}
                  </span>
                </span>
                {canSetControls ? (
                  <>
                    <select
                      aria-label={`Status of ${c.controlId} at this site`}
                      value={c.status}
                      disabled={busyControl === c.controlId}
                      onChange={(e) => setControl(c.controlId, e.target.value)}
                      style={{ ...inputStyle, width: "auto" }}
                    >
                      {Object.entries(CONTROL_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                    <button type="button" disabled={busyControl === c.controlId} onClick={() => setControl(c.controlId, null)}
                      title="Clear the site's result: the control then stands here as it does for the organisation"
                      style={{ fontSize: 11, padding: "3px 8px", borderRadius: 3, border: `1px solid ${theme.color.border}`, background: "transparent", color: theme.color.muted, cursor: "pointer" }}>
                      Clear
                    </button>
                  </>
                ) : (
                  <StatusTag tone={st.tone}>{st.label}</StatusTag>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

